import { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/context/AuthContext";

const ENTRY_LABELS = {
  learner_self_reported: "Learner self-reported",
  instructor_led_lesson: "Instructor-led lesson",
  supervised_practice: "Supervised practice",
  completed_instructor_lesson: "Completed instructor lesson",
};

function statusLabel(entry) {
  if (entry.status === "void") return "Voided";
  if (entry.status === "confirmed") return "Confirmed";
  return "Pending confirmation";
}

export default function InstructorPracticeHoursPanel({ learnerId, programmeId }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [busyId, setBusyId] = useState("");
  const [reason, setReason] = useState("");
  const [correctId, setCorrectId] = useState("");
  const [correctMinutes, setCorrectMinutes] = useState(60);

  const load = useCallback(() => {
    if (!learnerId) return Promise.resolve();
    return api
      .get("/practice-hours/instructor/learner/summary", {
        params: { learner_id: learnerId, programme_id: programmeId || "programme-nz-road-code" },
      })
      .then((res) => setData(res.data))
      .catch((err) => setError(formatApiError(err)));
  }, [learnerId, programmeId]);

  useEffect(() => {
    load();
  }, [load]);

  async function act(entryId, action) {
    setBusyId(entryId);
    setError("");
    try {
      await api.post("/practice-hours/instructor/entries/confirm", {
        entry_id: entryId,
        action,
        reason,
      });
      setReason("");
      await load();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusyId("");
    }
  }

  async function correct(entryId) {
    setBusyId(entryId);
    setError("");
    try {
      await api.post("/practice-hours/instructor/entries/correct", {
        entry_id: entryId,
        duration_minutes: Number(correctMinutes),
        reason: reason || "Duration correction",
      });
      setCorrectId("");
      setReason("");
      await load();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusyId("");
    }
  }

  if (!learnerId) return null;
  if (!data) return null;

  const s = data.summary || {};
  const entries = (data.entries || []).filter((e) => e.status !== "void");

  return (
    <section className="card space-y-3" data-testid="instructor-practice-hours">
      <h3 className="font-semibold text-sm">{data.config?.label || "GDI practice target"}</h3>
      <p className="text-xs text-muted-foreground">
        Target {s.target_hours ?? 60} h (effective from {data.config?.effective_from || "—"}). Not a statement of
        legal requirement.
      </p>
      <p className="text-sm">
        Confirmed <strong>{s.credited_hours ?? 0}</strong> h · Unconfirmed{" "}
        <strong>{Math.round((s.unconfirmed_minutes || 0) / 60)}</strong> h · Remaining{" "}
        <strong>{s.remaining_hours ?? 0}</strong> h
      </p>
      <label className="text-xs block">
        Reason for confirm / void / correct
        <input className="input mt-1" value={reason} onChange={(e) => setReason(e.target.value)} />
      </label>
      <ul className="space-y-2 text-sm">
        {entries.map((e) => (
          <li key={e.id} className="border rounded p-2" data-testid={`practice-entry-${e.id}`}>
            <div className="flex flex-wrap gap-2 justify-between">
              <span>
                {e.practice_date} · {e.duration_minutes} min ·{" "}
                {ENTRY_LABELS[e.source_kind] || ENTRY_LABELS[e.entry_type] || e.entry_type}
                {e.source_training_record_id ? ` · session ${String(e.source_training_record_id).slice(0, 8)}` : ""}
              </span>
              {e.reconciliation_needed && (
                <p className="text-xs text-amber-700">Reconciliation: overlapping manual entry — review before totalling.</p>
              )}
              <span className="text-muted-foreground">{statusLabel(e)}</span>
            </div>
            <p className="text-xs text-muted-foreground">
              Recorded by {e.recorded_by_role || "unknown"}
              {e.target_hours_at_record != null ? ` · target at record: ${e.target_hours_at_record} h` : ""}
            </p>
            {(e.audit || []).length > 0 && (
              <details className="text-xs mt-1">
                <summary>Audit ({e.audit.length})</summary>
                <ul className="list-disc ml-4">
                  {e.audit.map((a) => (
                    <li key={a.id}>
                      {a.at}: {a.action} {a.reason ? `— ${a.reason}` : ""}
                    </li>
                  ))}
                </ul>
              </details>
            )}
            {e.status === "pending" && (
              <div className="flex flex-wrap gap-2 mt-2">
                <button
                  type="button"
                  className="btn-primary text-xs"
                  disabled={busyId === e.id}
                  onClick={() => act(e.id, "confirm")}
                >
                  Confirm
                </button>
                <button
                  type="button"
                  className="btn-outline text-xs"
                  disabled={busyId === e.id}
                  onClick={() => act(e.id, "void")}
                >
                  Void
                </button>
              </div>
            )}
            {e.status === "confirmed" && (
              <div className="flex flex-wrap gap-2 mt-2 items-end">
                {correctId === e.id ? (
                  <>
                    <label className="text-xs">
                      New minutes
                      <input
                        className="input w-20"
                        type="number"
                        min={1}
                        value={correctMinutes}
                        onChange={(ev) => setCorrectMinutes(ev.target.value)}
                      />
                    </label>
                    <button type="button" className="btn-outline text-xs" onClick={() => correct(e.id)}>
                      Save correction
                    </button>
                    <button type="button" className="btn-outline text-xs" onClick={() => setCorrectId("")}>
                      Cancel
                    </button>
                  </>
                ) : (
                  <button type="button" className="btn-outline text-xs" onClick={() => setCorrectId(e.id)}>
                    Correct duration
                  </button>
                )}
              </div>
            )}
          </li>
        ))}
      </ul>
      {error && <p className="text-destructive text-sm">{error}</p>}
    </section>
  );
}
